<script setup>
    import __ from '@components/../mixins/locale'
    import axios from 'axios'
    import feedback from '@components/Feedback.vue';
    import { throttle } from 'throttle-debounce';
    import { onBeforeMount, onMounted, reactive, ref, watch } from 'vue'

    const props = defineProps({
        userId: {
            type: Number,
            default () {
                return null
            },
            required: false
        },
        type: {
            type: Number,
            required: true
        },
        contextualId: {
            type: Number,
            required: true
        },
        specificCommentId: {
            type: Number,
            required: false,
            default () {
                return 0
            }
        },
        postCommentOnly: {
            type: Boolean,
            required: false,
            default () {
                return false
            }
        },
        specificCommentIdToHighlight: {
            type: Number,
            required: false,
            default () {
                return 0
            }
        },
        preloadComments: {
            type: Array,
            required: false,
            default () {
                return null;
            }
        },
        isPhotoComment: {
            type: Boolean,
            required: false,
            default () {
                return false
            }
        },
        anchorLinkId: {
            type: String,
            required: false,
            default () {
                return 'comments'
            }
        }
    });

    const user = ref(props.userId);
    
    const commentContent = ref('');
    
    const formComments = reactive({
        parent_id: []
    });

    const formCommentsPosted = reactive({
        parent_id: []
    });

    const formCommentsError = reactive({
        parent_id: []
    });

    const formCommentsLoading = reactive({
        parent_id: []
    });

    const commentAnonymous = ref(false);

    const commentPosted = ref(false);

    const comments = ref([]);

    const error = ref(false);

    const loading = ref(false);

    const commentElement = ref(null);

    const isUserSet = () => {
        if(user.value) {
            return user.value;
        }
        return null;
    }

    const getUsername = (username) => {
        var response = __('by') + ' ';
        if(!username) {
            return response + __('anonymous');
        }
        return response + '<a href="' + __('userRoute') + '/' + username + '">' + username + '</a>';
    }
    
    const submitComment = (parentId) => {
        if(user.value === null) {
            commentAnonymous.value = true;
        }
        var comment = '';
        if(!parentId) {
            loading.value = true;
            parentId = null;
            comment = commentContent.value;
        } else {
            formCommentsLoading.parent_id[parentId] = true;
            comment = formComments.parent_id[parentId];
        }
        axios.post(`/api/comment`, {
            'userId': user.value,
            'type': props.type,
            'contextualId': props.contextualId,
            'parentId': parentId,
            'anonymous': commentAnonymous.value,
            'comment': comment
        }).then(() => {
            if(!parentId) {
                commentPosted.value = true;
                error.value = '';
                loading.value = false;                    
            } else {
                formCommentsPosted.parent_id[parentId] = true;
                formCommentsError.parent_id[parentId] = '';
                formCommentsLoading.parent_id[parentId] = false;
            }
        }).catch((error) => {
            if(!parentId) {
                error.value = error.response.data;
                loading.value = false;
            } else {
                formCommentsError.parent_id[parentId] = error.response.data;
                formCommentsLoading.parent_id[parentId] = false;
            }
        });
    }

    const getComments = () => {
        if (props.preloadComments) {
            return false;
        }
        loading.value = true;
        var paramsObject;
        if(user.value === null) {
            commentAnonymous.value = true;
        }
        if (props.specificCommentId) {
            paramsObject = {
                type: props.type,
                contextualId: props.contextualId,
                specificCommentId: props.specificCommentId
            }
        } else {
            paramsObject = {
                type: props.type,
                contextualId: props.contextualId,
            }
        }
        axios.get(`/api/comment`, {
            params: paramsObject
        }).then(response => {
            comments.value = response.data;
            error.value = '';
            loading.value = false;
        }).catch((error) => {
            error.value = error.response.data;
            loading.value = false;
        });
    }

    onMounted(() => {
        if (props.postCommentOnly) {
            loading.value = false;
        } else if (props.preloadComments) {
            comments.value = props.preloadComments;
            loading.value = false;
        } else if (!props.isPhotoComment) {
            const throttleFunc = throttle(5000, () => {
                if(isInViewport(commentElement.value)) {
                    getComments();
                    window.removeEventListener('scroll', throttleFunc);
                }
            });
            window.addEventListener('scroll', throttleFunc);
        } else {
            getComments();
        }
    })

    watch(() => props.contextualId, () => {
        getComments();
    })
</script>

<template>
    <div ref="commentElement" class="jumbotron" style="padding: 1rem; margin-bottom: 0;" v-bind:class="[{ 'text-center': loading }]">
        <span :id="anchorLinkId" class="anchor-link"></span>
        <div v-if="loading">
            <div class="loading-indicator"><div></div><div></div><div></div></div>
        </div>
        <div v-else>
            <template v-if="specificCommentId == 0">
                <div v-if="commentPosted">
                    <h3>{{ __('Your comment will appear once moderated, thank you!') }}</h3>
                </div>
                <div v-else>
                    <div class="form-group">
                        <textarea class="form-control" v-model="commentContent" rows="2" :placeholder="(type == 1 ? __('Very much enjoyed reading this sexy story - Keep up the good work') : __('Post a comment')) + '...'"></textarea>
                    </div>
                    <p v-if="error" style="color: red; font-weight: bold;">{{ error }}</p>
                    <button type="button" v-bind:disabled="commentContent === ''" class="btn btn-primary" @click="submitComment()">{{ __('Submit') }}</button>
                </div>
            </template>
            <ul class="list-unstyled" v-if="!postCommentOnly">
                <template v-for="(comment, index) in comments">
                    <li v-if="!comment.parent_id" class="media my-4 shadow-sm p-3 bg-white rounded">
                        <div class="media-body">
                            <feedback :small-btn="true" :namespace="'red-flag' + comment.id" type="COMMENT_RED_FLAG" :user-id="isUserSet()" :contextual-id="comment.id"></feedback>
                            <p v-html="comment.comment"></p>
                            <p class="mb-3 comment-signature" v-html="getUsername(comment.username)"></p>
                            <div v-if="!comment.parent_id" class="form-row">
                                <div v-if="formCommentsLoading.parent_id[comment.id]">
                                    <div class="loading-indicator"><div></div><div></div><div></div></div>
                                </div>
                                <div v-else>
                                    <div v-if="formCommentsPosted.parent_id[comment.id]">
                                        <h3>{{ __('Your comment will appear once moderated, thank you!') }}</h3>
                                    </div>
                                    <div v-else>
                                        <p v-if="formCommentsError.parent_id[comment.id]" style="color: red; font-weight: bold;">{{ formCommentsError.parent_id[comment.id] }}</p>
                                        <div class="form-inline">
                                            <div class="form-group mb-2">
                                                <input class="form-control" v-model="formComments.parent_id[comment.id]" :placeholder="__('Good comment')">
                                            </div>
                                            <button type="button" v-bind:disabled="!formComments.parent_id[comment.id] || formComments.parent_id[comment.id] === ''" class="btn btn-primary btn-md mb-2 ml-1" @click="submitComment(comment.id)">{{ __('Reply') }}</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <template v-for="(comment2, index) in comments">
                                <div v-if="comment2.parent_id === comment.id && (specificCommentIdToHighlight == 0 || specificCommentIdToHighlight != 0 && specificCommentIdToHighlight == comment2.id)" class="media ml-4 my-1 p-3 bg-white rounded" :class="[specificCommentIdToHighlight == 0 ? 'shadow-sm' : '', specificCommentIdToHighlight == comment2.id ? 'shadow-lg' : '']">
                                    <div class="media-body">
                                        <feedback :small-btn="true" :namespace="'red-flag' + comment2.id" type="COMMENT_RED_FLAG" :user-id="isUserSet()" :contextual-id="comment2.id"></feedback>
                                        <p v-html="comment2.comment"></p>
                                        <p class="comment-signature" v-html="getUsername(comment2.username)"></p>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>